import { ApiTranslation } from '../api-translation';
import { ToWinTooltip, useToWinQuote } from '../to-win-tooltip';
import { getOptionTone } from './option-tone';
import { useTradeGate } from '@/client/hooks/use-trade-gate';
import { useModelStore } from '@/client/store/model-store';
import { OptionSide, useTradeParamsStore } from '@/client/store/trade-store';
import { cn } from '@/client/utils';
import { HtmlHTMLAttributes, MouseEvent, ReactNode, useMemo, useRef } from 'react';
import useHoverDirty from 'react-use/lib/useHoverDirty';

type OptionButtonProps = HtmlHTMLAttributes<HTMLButtonElement> & {
  marketAddress: string;
  marketDecimal: number;
  optionAddress: string;
  optionName: string;
  optionDescription: string;
  optionPrice: number;
  optionDecimal: number;
  active?: boolean;
  openTrade?: boolean;
  before?: ReactNode;
  after?: ReactNode;
  showDescription?: boolean;
  children?: ReactNode;
  /** For multi-option markets: specify yes/no side */
  optionSide?: OptionSide;
  /** API fallback for when on-chain options() reverts */
  apiOptionList?: Array<{ optionAddress: string }>;
};
export const OptionButton = ({
  marketAddress,
  marketDecimal,
  optionAddress,
  optionName,
  optionDescription,
  optionDecimal,
  active,
  openTrade = true,
  className,
  onClick,
  before,
  after,
  showDescription = true,
  children,
  optionSide,
  apiOptionList,
  ...attrs
}: OptionButtonProps) => {
  const ref = useRef<HTMLButtonElement>(null);
  const isHover = useHoverDirty(ref);
  const toWinQuote = useToWinQuote({
    enabled: isHover,
    marketAddress,
    marketDecimal,
    optionAddress,
    optionDecimal,
    apiOptionList,
  });
  const tone = useMemo(
    () => getOptionTone(optionName) ?? getOptionTone(optionDescription) ?? getOptionTone(optionSide),
    [optionDescription, optionName, optionSide]
  );
  const setTradeOpen = useModelStore(state => state.setTradeOpen);
  const setTradeParams = useTradeParamsStore(state => state.setTradeParams);
  const tradeGate = useTradeGate();
  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    onClick?.(e);
    if (openTrade) {
      e.preventDefault();
      e.stopPropagation();
      // Gate: not signed in → login; DID-only (no wallet) → connect wallet.
      if (!tradeGate()) return;
      setTradeOpen(true);
      setTradeParams({ type: 'buy', marketAddress, optionAddress, optionSide, amount: BigInt(0) });
    }
  };
  return (
    <ToWinTooltip
      marketAddress={marketAddress}
      marketDecimal={marketDecimal}
      optionDecimal={optionDecimal}
      optionAddress={optionAddress}
      apiOptionList={apiOptionList}
    >
      <button
        ref={ref}
        type="button"
        {...attrs}
        className={cn(
          'rounded-1.5 flex-shrink-0 px-4 py-3 truncate flex items-center justify-center transition-colors',
          // Yes/Option1 - Green
          tone === 'yes' && 'text-emerald-600 bg-emerald-500/15',
          tone === 'yes' && 'hover:bg-emerald-600 hover:text-white',
          tone === 'yes' && active && 'bg-emerald-600 text-white',
          // No/Option2 - Red  
          tone === 'no' && 'text-red-500 bg-red-500/15',
          tone === 'no' && 'hover:bg-red-600 hover:text-white',
          tone === 'no' && active && 'bg-red-600 text-white',
          className
        )}
        onClick={handleClick}
      >
        {isHover ? (
          toWinQuote.label ?? (toWinQuote.isLoading ? '...' : optionDescription || optionName)
        ) : (
          <>
            {before}
            {children ? (
              children
            ) : showDescription ? (
              <span className="mx-1">
                <ApiTranslation>{optionDescription || optionName}</ApiTranslation>
              </span>
            ) : null}
            {after}
          </>
        )}
      </button>
    </ToWinTooltip>
  );
};
